import { Bell, CheckCheck } from "lucide-react";
import * as React from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  type NotificationItem,
} from "@/services/notifications";

/** 通知轮询间隔：进入页面拉取一次 + 每 60s 轮询（组件卸载清理定时器）。 */
const POLL_INTERVAL_MS = 60_000;

/**
 * 通知铃铛：未读数徽标 + 下拉列表（单条已读 / 全部已读）。
 *
 * 数据来自 Facet（经后端 /api/v1/notifications 代理，SSO 会话 token 由
 * apiClient 拦截器自动附带）。轮询在组件卸载时清理，避免悬挂定时器。
 */
export function NotificationBell({ className }: { className?: string }) {
  const [items, setItems] = React.useState<NotificationItem[]>([]);
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      setItems(await listNotifications());
    } catch {
      // 拉取失败保持现有列表：轮询期间 Facet 偶发不可用不应闪断
    } finally {
      setLoading(false);
    }
  }, []);

  // 进入页面拉取 + 60s 轮询；组件卸载清理定时器
  React.useEffect(() => {
    void load();
    const timer = window.setInterval(() => void load(), POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [load]);

  const unreadCount = items.filter((item) => !item.is_read).length;

  const handleRead = async (id: string) => {
    try {
      await markNotificationRead(id);
      setItems((prev) => prev.map((item) => (item.id === id ? { ...item, is_read: true } : item)));
    } catch {
      // best-effort：失败保持现状
    }
  };

  const handleReadAll = async () => {
    try {
      await markAllNotificationsRead();
      setItems((prev) => prev.map((item) => ({ ...item, is_read: true })));
    } catch {
      // best-effort
    }
  };

  return (
    <div className={cn("relative", className)} data-testid="notification-bell">
      <Button
        variant="ghost"
        size="icon"
        className="relative"
        aria-label={`通知（${unreadCount} 条未读）`}
        data-testid="notification-bell-trigger"
        onClick={() => setOpen((v) => !v)}
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span
            className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold text-white"
            data-testid="notification-bell-badge"
          >
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </Button>
      {open && (
        <>
          {/* 点击外部关闭 */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setOpen(false)}
            data-testid="notification-bell-backdrop"
          />
          <div
            className="absolute right-0 z-50 mt-2 w-80 max-w-[calc(100vw-2rem)] overflow-hidden rounded-lg border bg-popover text-popover-foreground shadow-lg"
            data-testid="notification-bell-panel"
          >
            <div className="flex items-center justify-between border-b px-3 py-2">
              <span className="text-sm font-semibold">通知</span>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                onClick={() => void handleReadAll()}
                data-testid="notification-mark-all-read"
              >
                <CheckCheck className="mr-1 h-3.5 w-3.5" />
                全部已读
              </Button>
            </div>
            <ul className="max-h-80 overflow-auto">
              {items.length === 0 && (
                <li
                  className="px-3 py-6 text-center text-sm text-muted-foreground"
                  data-testid="notification-empty"
                >
                  {loading ? "加载中…" : "暂无通知"}
                </li>
              )}
              {items.map((item) => (
                <li
                  key={item.id}
                  className={cn("border-b px-3 py-2 last:border-b-0", !item.is_read && "bg-muted/40")}
                  data-testid={`notification-item-${item.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">{item.title}</p>
                      <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{item.content}</p>
                    </div>
                    {!item.is_read && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 shrink-0 text-xs"
                        onClick={() => void handleRead(item.id)}
                        data-testid={`notification-mark-read-${item.id}`}
                      >
                        已读
                      </Button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </div>
  );
}
